import { useEffect, useState } from 'react';
import { usePaperPositionStore } from '../stores/paperPositionStore';
import { useAuthStore } from '../stores/authStore';
import socketManager from '../lib/socket';
import api from '../lib/api';
import {
  TrendingUp,
  TrendingDown,
  X,
  Shield,
  Target,
  ExternalLink,
  Loader2,
  Filter,
  Users,
  RefreshCw,
  Trash2,
  Download
} from 'lucide-react';

function PaperPositionCard({ position, onClose, onUpdateStopLoss, onUpdateTakeProfit }) {
  const [showSettings, setShowSettings] = useState(false);
  const [stopLossValue, setStopLossValue] = useState(position.stopLoss?.triggerPrice || 0);
  const [takeProfitValue, setTakeProfitValue] = useState(position.takeProfit?.triggerPrice || 0);

  const copyConfig = position.copyConfigId;
  const copyLabel =
    (copyConfig && typeof copyConfig === 'object' && (copyConfig.traderName || copyConfig.traderAddress))
      ? `${copyConfig.traderName || 'Trader'}${copyConfig.traderAddress ? ` (${copyConfig.traderAddress.slice(0, 6)}…${copyConfig.traderAddress.slice(-4)})` : ''}`
      : (position.copiedFrom?.traderAddress
        ? `${position.copiedFrom.traderAddress.slice(0, 6)}…${position.copiedFrom.traderAddress.slice(-4)}`
        : null);

  let avgPrice = Number(position.avgPrice) || 0;
  const currentPrice = Number(position.currentPrice) || 0;
  let size = Number(position.size) || 0;
  
  // Fallback calculations for missing data
  // If avgPrice is 0 but we have initialValue and size, calculate it
  if (avgPrice === 0 && size > 0 && Number(position.initialValue) > 0) {
    avgPrice = Number(position.initialValue) / size;
  }
  // If size is 0 but we have initialValue and avgPrice, calculate it
  if (size === 0 && avgPrice > 0 && Number(position.initialValue) > 0) {
    size = Number(position.initialValue) / avgPrice;
  }
  // If avgPrice is still 0, try using currentPrice as fallback (better than showing $0)
  if (avgPrice === 0 && currentPrice > 0) {
    avgPrice = currentPrice;
  }
  const currentValue = Number.isFinite(Number(position.currentValue))
    ? Number(position.currentValue)
    : size * currentPrice;
  const costBasis = Number.isFinite(Number(position.initialValue))
    ? Number(position.initialValue)
    : size * avgPrice;
  const unrealizedPnL = Number.isFinite(Number(position.unrealizedPnL))
    ? Number(position.unrealizedPnL)
    : currentValue - costBasis;
  const pnlPercent = Number.isFinite(Number(position.unrealizedPnLPercent))
    ? Number(position.unrealizedPnLPercent)
    : (costBasis > 0 ? (unrealizedPnL / costBasis) * 100 : 0);
  const isProfit = pnlPercent >= 0;

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-medium truncate">{position.market?.question}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`badge ${position.market?.outcome === 'Yes' ? 'badge-success' : 'badge-danger'}`}>
              {position.market?.outcome}
            </span>
            <span className="badge badge-info">Paper</span>
            {position.copiedFrom?.traderAddress && (
              <span className="badge badge-info">Copied</span>
            )}
            {copyLabel && (
              <span className="badge badge-info">{copyLabel}</span>
            )}
            {/* Status badge for non-open positions */}
            {position.status && position.status !== 'open' && (
              <span className={`badge ${
                position.status === 'closed' ? 'bg-gray-600' :
                position.status === 'stopped_out' ? 'bg-red-600' :
                position.status === 'take_profit' ? 'bg-emerald-600' :
                position.status === 'market_resolved' ? 'bg-yellow-600' :
                'bg-gray-600'
              } text-white`}>
                {position.status === 'stopped_out' ? '🛑 Stopped' :
                 position.status === 'take_profit' ? '🎯 TP Hit' :
                 position.status === 'market_resolved' ? '📊 Resolved' :
                 position.status === 'closed' ? '✓ Closed' :
                 position.status}
              </span>
            )}
            <a
              href={`https://polymarket.com/event/${position.market?.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
            >
              View <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <div className="text-right ml-4">
          <p className={`text-xl font-bold ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
            {isProfit ? '+' : ''}{pnlPercent.toFixed(1)}%
          </p>
          <p className="text-sm text-dark-400">
            {(unrealizedPnL >= 0 ? '+' : '')}${unrealizedPnL.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Size</p>
          <p className="font-medium">{size.toFixed(2)}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Avg Price</p>
          <p className="font-medium">${avgPrice.toFixed(2)}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Current</p>
          <p className="font-medium">${currentPrice.toFixed(2)}</p>
        </div>
        <div className="text-center p-2 bg-dark-700/50 rounded-lg">
          <p className="text-xs text-dark-400">Value</p>
          <p className="font-medium">${currentValue.toFixed(2)}</p>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        {position.stopLoss?.enabled && (
          <div className="flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-red-400" />
            <span className="text-dark-400">SL:</span>
            <span className="text-red-400">${position.stopLoss.triggerPrice?.toFixed(2)}</span>
          </div>
        )}
        {position.takeProfit?.enabled && (
          <div className="flex items-center gap-2 text-sm">
            <Target className="w-4 h-4 text-emerald-400" />
            <span className="text-dark-400">TP:</span>
            <span className="text-emerald-400">${position.takeProfit.triggerPrice?.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Actions - only show for open positions */}
      {position.status === 'open' && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="btn btn-secondary flex-1"
          >
            {showSettings ? 'Hide Settings' : 'Risk Settings'}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose(position._id);
            }}
            className="btn btn-danger flex-1"
          >
            Close Position
          </button>
        </div>
      )}

      {/* Closed position info */}
      {position.status !== 'open' && (
        <div className="text-center py-2 text-sm text-dark-400">
          {position.closedAt && (
            <span>Closed: {new Date(position.closedAt).toLocaleDateString()}</span>
          )}
          {position.closeReason && (
            <span className="ml-2">• {position.closeReason.replace(/_/g, ' ')}</span>
          )}
        </div>
      )}

      {showSettings && position.status === 'open' && (
        <div className="mt-4 pt-4 border-t border-dark-700 space-y-4 animate-fade-in">
          <div className="p-3 bg-dark-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-400" />
                Stop Loss
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={position.stopLoss?.enabled}
                  onChange={(e) => onUpdateStopLoss(position._id, {
                    enabled: e.target.checked,
                    type: 'percentage',
                    value: 20
                  })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-600"></div>
              </label>
            </div>
            {position.stopLoss?.enabled && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={stopLossValue}
                  onChange={(e) => setStopLossValue(parseFloat(e.target.value))}
                  className="input flex-1"
                  step="0.01"
                  min="0"
                  max="1"
                />
                <button
                  onClick={() => onUpdateStopLoss(position._id, {
                    enabled: true,
                    type: 'fixed',
                    value: position.avgPrice - stopLossValue
                  })}
                  className="btn btn-secondary"
                >
                  Update
                </button>
              </div>
            )}
          </div>

          <div className="p-3 bg-dark-700/50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-400" />
                Take Profit
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={position.takeProfit?.enabled}
                  onChange={(e) => onUpdateTakeProfit(position._id, {
                    enabled: e.target.checked,
                    type: 'percentage',
                    value: 50
                  })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
            {position.takeProfit?.enabled && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={takeProfitValue}
                  onChange={(e) => setTakeProfitValue(parseFloat(e.target.value))}
                  className="input flex-1"
                  step="0.01"
                  min="0"
                  max="1"
                />
                <button
                  onClick={() => onUpdateTakeProfit(position._id, {
                    enabled: true,
                    type: 'fixed',
                    value: takeProfitValue - position.avgPrice
                  })}
                  className="btn btn-secondary"
                >
                  Update
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PaperTrading() {
  const { user } = useAuthStore();
  const {
    positions,
    summary,
    isLoading,
    error,
    fetchPositions,
    fetchSummary,
    updateStopLoss,
    updateTakeProfit,
    closePosition,
    updatePosition
  } = usePaperPositionStore();

  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const raw = localStorage.getItem('betmimic.paper.filters');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.statusFilter || 'open';
    } catch (_) {
      return 'open';
    }
  });
  const [traderFilter, setTraderFilter] = useState(() => {
    try {
      const raw = localStorage.getItem('betmimic.paper.filters');
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.traderFilter || 'all';
    } catch (_) {
      return 'all';
    }
  });
  const [isClosing, setIsClosing] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(
        'betmimic.paper.filters',
        JSON.stringify({ statusFilter, traderFilter })
      );
    } catch (_) {
    }
  }, [statusFilter, traderFilter]);

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    if (user?._id) {
      socketManager.refreshPaperPositions(user._id);
    }
    await fetchPositions(statusFilter);
    await fetchSummary();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  useEffect(() => {
    // Always fetch fresh data from server on mount/filter change
    fetchPositions(statusFilter);
    fetchSummary();

    // Auto-refresh every 60 seconds to catch any missed updates (reduced from 30s to prevent flicker)
    const autoRefreshInterval = setInterval(() => {
      fetchPositions(statusFilter, false); // silent refresh
      fetchSummary();
    }, 60000);

    if (user?._id) {
      socketManager.subscribeToPositions(user._id);

      // Debounced refresh - prevents UI flicker from rapid socket events
      let refreshTimeout = null;
      const debouncedRefresh = () => {
        if (refreshTimeout) clearTimeout(refreshTimeout);
        refreshTimeout = setTimeout(() => {
          fetchPositions(statusFilter, false); // silent refresh
          fetchSummary();
        }, 5000); // Wait 5s after last event
      };

      const onPaperPositionUpdated = (position) => {
        updatePosition(position);
        debouncedRefresh();
      };
      const onPaperStopLoss = ({ position }) => {
        updatePosition(position);
        debouncedRefresh();
      };
      const onPaperTakeProfit = ({ position }) => {
        updatePosition(position);
        debouncedRefresh();
      };
      const onPositionClosed = () => debouncedRefresh();
      const onPaperTradeExecuted = () => debouncedRefresh();

      socketManager.on('paperPositionUpdated', onPaperPositionUpdated);
      socketManager.on('paperStopLossTriggered', onPaperStopLoss);
      socketManager.on('paperTakeProfitTriggered', onPaperTakeProfit);
      socketManager.on('positionClosed', onPositionClosed);
      socketManager.on('paperTradeExecuted', onPaperTradeExecuted);

      return () => {
        clearInterval(autoRefreshInterval);
        if (refreshTimeout) clearTimeout(refreshTimeout);
        socketManager.off('paperPositionUpdated', onPaperPositionUpdated);
        socketManager.off('paperStopLossTriggered', onPaperStopLoss);
        socketManager.off('paperTakeProfitTriggered', onPaperTakeProfit);
        socketManager.off('positionClosed', onPositionClosed);
        socketManager.off('paperTradeExecuted', onPaperTradeExecuted);
      };
    }

    // Cleanup interval even if no user
    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, [user?._id, statusFilter]);

  const handleClose = async (positionId) => {
    if (!confirm('Are you sure you want to close this paper position?')) return;

    setIsClosing(positionId);
    const result = await closePosition(positionId);
    setIsClosing(null);

    if (!result.success) {
      alert(result.error || 'Failed to close paper position');
    }
  };

  const handleStopLossUpdate = async (positionId, data) => {
    const result = await updateStopLoss(positionId, data);
    if (!result.success) {
      alert(result.error || 'Failed to update stop loss');
    }
  };

  const handleTakeProfitUpdate = async (positionId, data) => {
    const result = await updateTakeProfit(positionId, data);
    if (!result.success) {
      alert(result.error || 'Failed to update take profit');
    }
  };

  const handleCleanupOrphaned = async () => {
    if (!confirm('This will remove paper positions from deleted copy configs. Continue?')) return;
    try {
      const response = await api.delete('/paper/cleanup-orphaned');
      alert(response.data?.message || 'Cleanup complete');
      fetchPositions(statusFilter);
      fetchSummary();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to cleanup');
    }
  };

  const [isResetting, setIsResetting] = useState(false);

  const handleResetAll = async () => {
    const confirmed = confirm(
      '⚠️ WARNING: This will permanently delete ALL your paper trading data including:\n\n' +
      '• All open positions\n' +
      '• All closed positions\n' +
      '• All trade history\n' +
      '• All P&L records\n\n' +
      'This action CANNOT be undone. Are you sure you want to reset?'
    );
    if (!confirmed) return;

    // Double confirmation for safety
    const doubleConfirm = confirm('Are you ABSOLUTELY sure? Type OK to confirm reset.');
    if (!doubleConfirm) return;

    setIsResetting(true);
    try {
      const response = await api.delete('/paper/reset');
      alert(`✅ ${response.data?.message}\n\nDeleted ${response.data?.deletedPositions || 0} positions and ${response.data?.deletedTrades || 0} trades.`);
      fetchPositions(statusFilter);
      fetchSummary();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to reset paper trading data');
    } finally {
      setIsResetting(false);
    }
  };

  const getTraderOptionForPosition = (position) => {
    const copyConfig = position?.copyConfigId;
    const cfgObj = copyConfig && typeof copyConfig === 'object' ? copyConfig : null;
    const cfgId = cfgObj?._id || (typeof copyConfig === 'string' ? copyConfig : null);
    const cfgTraderAddress = cfgObj?.traderAddress;
    const cfgTraderName = cfgObj?.traderName;
    if (cfgId) {
      const shortAddr = cfgTraderAddress
        ? ` (${cfgTraderAddress.slice(0, 6)}…${cfgTraderAddress.slice(-4)})`
        : '';
      return {
        key: `config:${cfgId}`,
        label: `${cfgTraderName || 'Trader'}${shortAddr}`
      };
    }

    const addr = position?.copiedFrom?.traderAddress;
    if (addr) {
      return {
        key: `addr:${addr.toLowerCase()}`,
        label: `${addr.slice(0, 6)}…${addr.slice(-4)}`
      };
    }

    return null;
  };

  const traderOptions = (() => {
    const map = new Map();
    for (const p of positions || []) {
      const opt = getTraderOptionForPosition(p);
      if (!opt) continue;
      if (!map.has(opt.key)) {
        map.set(opt.key, opt.label);
      }
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  })();

  const filteredPositions = (positions || [])
    .filter((p) => statusFilter === 'all' || (p.status || 'open') === statusFilter)
    .filter((p) => {
      if (traderFilter === 'all') return true;
      const [kind, value] = (traderFilter || '').split(':');
      if (!kind || !value) return true;

      if (kind === 'config') {
        const copyConfig = p?.copyConfigId;
        const cfgObj = copyConfig && typeof copyConfig === 'object' ? copyConfig : null;
        const cfgId = (cfgObj?._id || (typeof copyConfig === 'string' ? copyConfig : null))?.toString();
        return cfgId === value;
      }

      if (kind === 'addr') {
        const addr = p?.copiedFrom?.traderAddress?.toLowerCase();
        return !!addr && addr === value.toLowerCase();
      }

      return true;
    });

  const selectedTraderLabel = traderFilter === 'all'
    ? 'All Traders'
    : (traderOptions.find((o) => o.value === traderFilter)?.label || 'Selected Trader');

  const selectedSummary = (() => {
    const s = {
      positions: filteredPositions.length,
      totalValue: 0,
      totalUnrealizedPnL: 0,
      totalRealizedPnL: 0
    };
    for (const p of filteredPositions) {
      const size = Number(p?.size) || 0;
      const currentPrice = Number(p?.currentPrice) || 0;
      const avgPrice = Number(p?.avgPrice) || 0;
      const currentValue = Number.isFinite(Number(p?.currentValue))
        ? Number(p.currentValue)
        : size * currentPrice;
      const costBasis = Number.isFinite(Number(p?.initialValue))
        ? Number(p.initialValue)
        : size * avgPrice;
      const unrealized = Number.isFinite(Number(p?.unrealizedPnL))
        ? Number(p.unrealizedPnL)
        : currentValue - costBasis;
      const realized = Number.isFinite(Number(p?.realizedPnL)) ? Number(p.realizedPnL) : 0;

      s.totalValue += currentValue || 0;
      s.totalUnrealizedPnL += unrealized || 0;
      s.totalRealizedPnL += realized || 0;
    }
    return s;
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Paper Trading</h1>
          <p className="text-dark-400">Simulated positions and trades (no real orders)</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-dark-400" />
            <select
              value={traderFilter}
              onChange={(e) => setTraderFilter(e.target.value)}
              className="input"
            >
              <option value="all">All Traders</option>
              {traderOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-dark-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="stopped_out">Stopped Out</option>
              <option value="take_profit">Take Profit</option>
              <option value="all">All</option>
            </select>
          </div>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn btn-secondary flex items-center gap-2"
            title="Refresh positions"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <a
            href="/api/paper/trades/export"
            download
            className="btn btn-secondary flex items-center gap-2"
            title="Export trades to CSV"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </a>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-3 bg-dark-800/50 border border-dark-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-dark-300">Cleanup Orphaned Positions</p>
              <p className="text-xs text-dark-500">Remove positions from deleted copy configs</p>
            </div>
            <button
              onClick={handleCleanupOrphaned}
              className="btn btn-secondary"
            >
              Run Cleanup
            </button>
          </div>
        </div>

        <div className="card p-3 bg-red-900/20 border border-red-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-300">Reset All Paper Trading</p>
              <p className="text-xs text-red-400/70">Delete all positions, trades & P&L history</p>
            </div>
            <button
              onClick={handleResetAll}
              disabled={isResetting}
              className="btn bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              {isResetting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Reset All
            </button>
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="card text-center">
            <p className="text-sm text-dark-400">Open Positions</p>
            <p className="text-2xl font-bold">{summary.byStatus?.open || 0}</p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-dark-400">Total Value</p>
            <p className="text-2xl font-bold">${(summary.totalValue || 0).toFixed(2)}</p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-dark-400">Unrealized P&L</p>
            <p className={`text-2xl font-bold ${(summary.totalUnrealizedPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              ${(summary.totalUnrealizedPnL || 0).toFixed(2)}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-dark-400">Realized P&L</p>
            <p className={`text-2xl font-bold ${(summary.totalRealizedPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              ${(summary.totalRealizedPnL || 0).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {traderFilter !== 'all' && (
        <div className="grid grid-cols-4 gap-4">
          <div className="card text-center">
            <p className="text-sm text-dark-400">Selected Trader</p>
            <p className="text-base font-semibold truncate">{selectedTraderLabel}</p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-dark-400">Positions</p>
            <p className="text-2xl font-bold">{selectedSummary.positions || 0}</p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-dark-400">Unrealized P&L</p>
            <p className={`text-2xl font-bold ${(selectedSummary.totalUnrealizedPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              ${(selectedSummary.totalUnrealizedPnL || 0).toFixed(2)}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-dark-400">Realized P&L</p>
            <p className={`text-2xl font-bold ${(selectedSummary.totalRealizedPnL || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              ${(selectedSummary.totalRealizedPnL || 0).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="card border border-red-500/30 bg-red-500/10">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      ) : filteredPositions.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-dark-400">No paper positions found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredPositions.map(position => (
            <div key={position._id} className="relative">
              {isClosing === position._id && (
                <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center z-10">
                  <Loader2 className="w-6 h-6 animate-spin text-white" />
                </div>
              )}
              <PaperPositionCard
                position={position}
                onClose={handleClose}
                onUpdateStopLoss={handleStopLossUpdate}
                onUpdateTakeProfit={handleTakeProfitUpdate}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
